/**
 * Notification Context
 *
 * Provides global notification state including:
 * - Unread count
 * - WebSocket connection for real-time updates
 * - Toast notifications for new messages
 */

import {
  useGetUnreadCount,
  useListNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  getListNotificationsQueryKey,
  getGetUnreadCountQueryKey,
  type NotificationsNotificationResponse,
} from '@smarthome/api';
import { useNotificationStream, type NotificationStreamEvent } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';

import { markAllNotificationsReadInCache, markNotificationReadInCache } from '../cache';

import { useToast } from '@/components/ui';

export interface NotificationContextValue {
  /** Current unread count */
  unreadCount: number;
  /** Whether the count is loading */
  isLoadingCount: boolean;
  /** Recent notifications for dropdown */
  recentNotifications: NotificationsNotificationResponse[];
  /** Whether notifications are loading */
  isLoadingNotifications: boolean;
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Mark a single notification as read */
  markAsRead: (id: string) => void;
  /** Mark all notifications as read */
  markAllAsRead: () => void;
  /** Whether mark as read is in progress */
  isMarkingAsRead: boolean;
  /** Refetch notifications */
  refetchNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Fetch unread count
  const { data: countData, isLoading: isLoadingCount } = useGetUnreadCount({
    query: {
      refetchInterval: 60000, // Refetch every minute as fallback
    },
  });

  // Fetch recent notifications for dropdown (limit to 10)
  const {
    data: notificationsData,
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications,
  } = useListNotifications({ limit: 10, offset: 0 });

  // Mark as read mutation
  const { mutate: markAsReadMutation, isPending: isMarkingRead } = useMarkAsRead({
    mutation: {
      onMutate: ({ id }) => {
        markNotificationReadInCache(queryClient, id, new Date().toISOString());
      },
      onSuccess: () => {
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
    },
  });

  // Mark all as read mutation
  const { mutate: markAllAsReadMutation, isPending: isMarkingAllRead } = useMarkAllAsRead({
    mutation: {
      onMutate: () => {
        markAllNotificationsReadInCache(queryClient, new Date().toISOString());
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
      onError: () => {
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
      },
    },
  });

  // Handle WebSocket events
  const handleNotificationEvent = useCallback(
    (event: NotificationStreamEvent) => {
      if (event.type === 'notification' && event.notification) {
        // Invalidate queries to fetch new data
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });

        // Show toast notification
        const notification = event.notification;
        const title = notification.title || 'New notification';
        const body = notification.body || '';
        const displayText = body.length > 100 ? `${body.substring(0, 97)}...` : body;
        const message = title !== 'New notification' ? `${title}: ${displayText}` : displayText;

        addToast({
          message: message || title,
          variant: 'info',
          duration: 5000,
        });

        // If there's a deep link, we could navigate on click
        // For now, the toast is just informational
      } else if (event.type === 'count_update' && event.count !== undefined) {
        // Update the count directly or invalidate
        queryClient.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() });
      }
    },
    [queryClient, addToast]
  );

  // Connect to WebSocket
  const { isConnected } = useNotificationStream({
    enabled: true,
    onEvent: handleNotificationEvent,
  });

  // Action handlers
  const markAsRead = useCallback(
    (id: string) => {
      markAsReadMutation({ id });
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation();
  }, [markAllAsReadMutation]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      unreadCount: countData?.count ?? 0,
      isLoadingCount,
      recentNotifications: notificationsData?.notifications ?? [],
      isLoadingNotifications,
      isConnected,
      markAsRead,
      markAllAsRead,
      isMarkingAsRead: isMarkingRead || isMarkingAllRead,
      refetchNotifications: () => refetchNotifications(),
    }),
    [
      countData?.count,
      isLoadingCount,
      notificationsData?.notifications,
      isLoadingNotifications,
      isConnected,
      markAsRead,
      markAllAsRead,
      isMarkingRead,
      isMarkingAllRead,
      refetchNotifications,
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
